import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House,
  QrCode,
  Printer,
  ChartBar,
  ChatCircleText,
  GearSix,
  SignOut,
  ShieldCheck,
} from "@phosphor-icons/react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/generate", label: "Generate QR", icon: QrCode, testId: "nav-generate" },
  { to: "/print", label: "Print Labels", icon: Printer, testId: "nav-print" },
  { to: "/reports", label: "Reports", icon: ChartBar, testId: "nav-reports" },
  { to: "/feedback", label: "Feedback", icon: ChatCircleText, testId: "nav-feedback" },
  { to: "/settings", label: "Settings", icon: GearSix, testId: "nav-settings" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header
        className="sticky top-0 z-40 bg-[#253c80] text-white shadow-md print:hide"
        data-testid="admin-header"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
            <div className="h-9 w-9 rounded-md bg-white/10 grid place-items-center ring-1 ring-white/20">
              <ShieldCheck weight="duotone" size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-extrabold tracking-tight text-base">TVS Genuine Parts</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/70">
                QR Generator Admin
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white text-[#253c80]"
                      : "text-white/85 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon size={16} weight="bold" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold">{user?.name || "Admin"}</div>
              <div className="text-[11px] text-white/70">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-2 text-sm font-medium transition-colors"
              data-testid="logout-button"
            >
              <SignOut size={16} weight="bold" />
              Logout
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden border-t border-white/10 px-2 py-2 overflow-x-auto flex gap-1">
          {nav.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testId}-mobile`}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
                  isActive ? "bg-white text-[#253c80]" : "text-white/85"
                }`
              }
            >
              <Icon size={14} weight="bold" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-8">{children}</main>
    </div>
  );
}
